import * as Diff from 'diff';
import type { StructuredPatchHunk } from 'diff';
import type { ThemedToken } from '@reflex/ui/lib/syntax-highlight';
import type { DiffContent } from '../types.js';

/*
 * Diff helpers for the transcript's Write/Edit views.
 *
 * Everything here runs on untrusted tool output — the file an agent wrote can
 * be megabytes, a single line, or binary — and it runs on the render path of a
 * list that re-renders while a turn streams. Three rules keep that safe:
 *
 * 1. Guards before diffs. The size checks are O(1) or a single allocation-free
 *    scan, so pathological content is rejected before any Myers work starts.
 * 2. Every diff has a ceiling. `maxEditLength` aborts a diff whose edit script
 *    is longer than we're willing to pay for and `timeout` aborts one that
 *    takes longer than we're willing to block for, so each caller degrades to
 *    "no answer" rather than to a slow answer.
 * 3. Diff once. Results are memoized per (immutable) diff content item, so the
 *    collapsed row's `+N -M` badge and the expanded diff share a single pass,
 *    and expanding or collapsing a row doesn't recompute either.
 */

/** Ceiling on a line diff's edit script: O(N·D) for the patch, ~O(D²) for the trimmed count, so bounding D bounds both. Past it we show a fallback instead of a diff. */
export const MAX_DIFF_EDIT_DISTANCE = 2_000;
/**
 * Wall-clock ceiling on a line diff, checked by jsdiff alongside
 * {@link MAX_DIFF_EDIT_DISTANCE}. The edit-distance ceiling bounds the *answer*
 * we're willing to render; this bounds the *search*, including the work spent
 * discovering that a diff is over that ceiling. Without it a pathological pair
 * of large files can hold the main thread for a second before being rejected.
 */
const MAX_DIFF_MILLISECONDS = 200;
/**
 * Ceiling on a single line's word diff. A pair that needs this many word edits
 * is not "the same line, lightly edited" — it would fail the changed-fraction
 * check below anyway — so aborting keeps the cost of the pairs we discard
 * proportional to the answer we'd have discarded, not to the line length.
 */
const MAX_WORD_DIFF_EDIT_DISTANCE = 32;
/** Character budget for one hunk's word diffs, spent per pair. A hunk of thousands of replaced lines stops highlighting rather than paying for every pair. */
const MAX_WORD_DIFF_CHARS_PER_HUNK = 100_000;
/** Consecutive unhighlightable pairs before a hunk gives up on word diffing, counted across the hunk's replace blocks. A hunk whose pairs keep coming back unrelated is a block rewrite, not a line-by-line edit. */
const MAX_CONSECUTIVE_WORD_DIFF_MISSES = 8;
/** Context lines kept around each hunk. */
const DIFF_CONTEXT_LINES = 3;

// ---------------------------------------------------------------------------
// Size guards
// ---------------------------------------------------------------------------

/** Above this many characters on either side, a diff is too large to render in full. */
export const MAX_DIFF_TEXT_LENGTH = 400_000;
/** Above this many characters on a single line, that line is too large to render in full. */
export const MAX_DIFF_LINE_LENGTH = 5_000;
/** Characters sniffed for NUL, matching the prefix `git diff` inspects — keeps the check O(1) however big the content is. */
const BINARY_SNIFF_LENGTH = 8_000;

/** Same NUL-byte heuristic `git diff` uses to tell text from binary content, over the same bounded prefix. */
export function isBinaryText(text: string): boolean {
  const end = Math.min(text.length, BINARY_SNIFF_LENGTH);
  for (let i = 0; i < end; i++) {
    if (text.charCodeAt(i) === 0) return true;
  }
  return false;
}

/**
 * Length of the first line longer than `limit`, or null if every line fits.
 * Walks newline to newline instead of `split('\n')`: splitting allocates a
 * string per line only to throw them all away, and the whole point of this
 * check is that the input may be enormous.
 */
function firstOversizedLineLength(text: string, limit: number): number | null {
  if (text.length <= limit) return null;
  let start = 0;
  for (;;) {
    const nl = text.indexOf('\n', start);
    const end = nl === -1 ? text.length : nl;
    if (end - start > limit) return end - start;
    if (nl === -1) return null;
    start = nl + 1;
  }
}

/** Why a diff fell back to a summary instead of rendering. */
export type DiffSizeIssue =
  | { kind: 'binary' }
  /** Total characters on the larger side. */
  | { kind: 'too-large'; charCount: number }
  /** Characters on the offending line. */
  | { kind: 'oversized-line'; charCount: number }
  | { kind: 'too-many-changes' };

/**
 * Guard against pathological diff content — binary data, a multi-hundred-KB
 * file, or a single line of millions of characters — that would otherwise blow
 * out the view with a full Shiki-highlighted, word-diffed render. Both sides
 * are checked, since either can independently be the problem (a large file
 * truncated to nothing, or the reverse), and the checks run cheapest-first: a
 * bounded NUL sniff, then O(1) length comparisons, then the one scan that has
 * to look at every character.
 */
export function detectDiffSizeIssue(
  oldText: string | null | undefined,
  newText: string,
): DiffSizeIssue | null {
  const oldStr = oldText ?? '';
  if (isBinaryText(oldStr) || isBinaryText(newText)) return { kind: 'binary' };

  const longest = Math.max(oldStr.length, newText.length);
  if (longest > MAX_DIFF_TEXT_LENGTH) return { kind: 'too-large', charCount: longest };

  const oldLine = firstOversizedLineLength(oldStr, MAX_DIFF_LINE_LENGTH);
  if (oldLine !== null) return { kind: 'oversized-line', charCount: oldLine };
  const newLine = firstOversizedLineLength(newText, MAX_DIFF_LINE_LENGTH);
  if (newLine !== null) return { kind: 'oversized-line', charCount: newLine };

  return null;
}

// ---------------------------------------------------------------------------
// Line counts
// ---------------------------------------------------------------------------

/** Line-count summary for a Write/Edit tool row's `+N -M` badge. */
export interface DiffLineCounts {
  added: number;
  removed: number;
}

/** Lines in `text`, counted the way the diff tokenizer does: a trailing newline ends the last line rather than starting an empty one. */
function countLines(text: string): number {
  if (text.length === 0) return 0;
  let lines = 0;
  let at = text.indexOf('\n');
  while (at !== -1) {
    lines++;
    at = text.indexOf('\n', at + 1);
  }
  return text.charCodeAt(text.length - 1) === 10 ? lines : lines + 1;
}

/**
 * Characters compared per block on the fast path below. Native string
 * comparison runs an order of magnitude faster per byte than a `charCodeAt`
 * loop, so identical content is skipped a block at a time; the block is small
 * enough that walking the one block containing the mismatch stays trivial.
 */
const COMPARE_BLOCK_LENGTH = 4_096;

/** Length of the longest common prefix of `a` and `b`. */
function commonPrefixLength(a: string, b: string): number {
  const max = Math.min(a.length, b.length);
  let at = 0;
  while (at + COMPARE_BLOCK_LENGTH <= max) {
    const next = at + COMPARE_BLOCK_LENGTH;
    if (a.slice(at, next) !== b.slice(at, next)) break;
    at = next;
  }
  while (at < max && a.charCodeAt(at) === b.charCodeAt(at)) at++;
  return at;
}

/** Length of the longest common suffix of `a` and `b`, looking at no more than `limit` characters. */
function commonSuffixLength(a: string, b: string, limit: number): number {
  let at = 0;
  while (at + COMPARE_BLOCK_LENGTH <= limit) {
    const next = at + COMPARE_BLOCK_LENGTH;
    if (a.slice(a.length - next, a.length - at) !== b.slice(b.length - next, b.length - at)) break;
    at = next;
  }
  while (at < limit && a.charCodeAt(a.length - 1 - at) === b.charCodeAt(b.length - 1 - at)) at++;
  return at;
}

/**
 * Length of the longest common prefix of `a` and `b` that covers whole lines.
 * Because the prefix is common, the line start it backs up to is a line start
 * on both sides, so trimming there can't change how the remainder lines up.
 */
function commonLinePrefixLength(a: string, b: string): number {
  const shared = commonPrefixLength(a, b);
  // `lastIndexOf` clamps a negative start to 0 rather than searching nothing,
  // so an empty common prefix has to short-circuit: otherwise a leading '\n'
  // in `a` that `b` doesn't share would count as a common line.
  return shared === 0 ? 0 : a.lastIndexOf('\n', shared - 1) + 1;
}

/**
 * Length of the longest common suffix of `a` and `b` that covers whole lines
 * and consumes at most `limit` characters, so it can't overlap an
 * already-trimmed prefix.
 *
 * Unlike a common prefix, a common suffix says nothing about the characters in
 * front of it, so the boundary has to be a line start in *both* strings — cut
 * `foo` off `a\nfoo` and `a\nbarfoo` and the remainders no longer tokenize into
 * the lines they came from. That two-sided check only matters at the very edge
 * of the common region: any shorter suffix starts inside it, where the two
 * strings agree character for character, so one newline search finds the
 * longest one that qualifies.
 */
function commonLineSuffixLength(a: string, b: string, limit: number): number {
  const raw = commonSuffixLength(a, b, limit);
  const aStart = a.length - raw;
  const bStart = b.length - raw;
  const aAtLineStart = aStart === 0 || a.charCodeAt(aStart - 1) === 10;
  const bAtLineStart = bStart === 0 || b.charCodeAt(bStart - 1) === 10;
  if (aAtLineStart && bAtLineStart) return raw;

  const firstNewline = a.indexOf('\n', aStart);
  return firstNewline === -1 ? 0 : a.length - 1 - firstNewline;
}

/**
 * Added/removed line counts for a full old/new text pair, or null when the
 * change is too large to count within {@link MAX_DIFF_EDIT_DISTANCE}.
 *
 * The interesting part is the work it avoids. A new file (`oldText === ''`) is
 * the common case for Write and needs no diff at all, just a newline count. For
 * a real edit, the identical head and tail of the two files are trimmed on line
 * boundaries first — an edit is usually a few lines in the middle of a big
 * file, and a minimal edit script over that middle has exactly the same
 * added/removed counts as one over the whole file. What's left goes through
 * `diffLines` rather than `structuredPatch`, which would additionally
 * materialize every hunk line as a prefixed string we'd immediately discard.
 */
export function countDiffLines(oldText: string, newText: string): DiffLineCounts | null {
  if (oldText === newText) return { added: 0, removed: 0 };
  if (oldText.length === 0) return { added: countLines(newText), removed: 0 };
  if (newText.length === 0) return { added: 0, removed: countLines(oldText) };

  const prefix = commonLinePrefixLength(oldText, newText);
  const suffixLimit = Math.min(oldText.length, newText.length) - prefix;
  const suffix = commonLineSuffixLength(oldText, newText, suffixLimit);
  const parts = Diff.diffLines(
    oldText.slice(prefix, oldText.length - suffix),
    newText.slice(prefix, newText.length - suffix),
    { maxEditLength: MAX_DIFF_EDIT_DISTANCE, timeout: MAX_DIFF_MILLISECONDS },
  );
  if (!parts) return null;

  let added = 0;
  let removed = 0;
  for (const part of parts) {
    if (part.added) added += part.count ?? 0;
    else if (part.removed) removed += part.count ?? 0;
  }
  return { added, removed };
}

/** Added/removed counts read off an already-computed patch — free once the hunks exist. */
function countHunkLines(hunks: readonly StructuredPatchHunk[]): DiffLineCounts {
  let added = 0;
  let removed = 0;
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      const prefix = line.charCodeAt(0);
      if (prefix === 43 /* + */) added++;
      else if (prefix === 45 /* - */) removed++;
    }
  }
  return { added, removed };
}

// ---------------------------------------------------------------------------
// Memoized per-diff analysis
// ---------------------------------------------------------------------------

interface DiffAnalysis {
  issue?: DiffSizeIssue | null;
  counts?: DiffLineCounts | null;
  /** Null once computed means the diff aborted on {@link MAX_DIFF_EDIT_DISTANCE}. */
  hunks?: StructuredPatchHunk[] | null;
  /** Per-hunk word-diff ranges, aligned with `hunks`. */
  wordRanges?: Array<Array<CharRange[] | undefined>>;
}

/**
 * Per-diff scratchpad. Transcript content items are immutable, so their
 * identity is a sound cache key, and a `WeakMap` keeps each entry alive exactly
 * as long as the transcript holds the item. This is what lets the always-visible
 * collapsed row and the expanded diff view share a single Myers pass, and what
 * keeps toggling a row open and shut from re-diffing on every toggle.
 */
const analysisCache = new WeakMap<DiffContent, DiffAnalysis>();

function analysisFor(diff: DiffContent): DiffAnalysis {
  let entry = analysisCache.get(diff);
  if (!entry) {
    entry = {};
    analysisCache.set(diff, entry);
  }
  return entry;
}

/** Memoized {@link detectDiffSizeIssue} for a diff content item. Callers must check this before asking for hunks or counts. */
export function getDiffSizeIssue(diff: DiffContent): DiffSizeIssue | null {
  const entry = analysisFor(diff);
  entry.issue ??= detectDiffSizeIssue(diff.oldText, diff.newText);
  return entry.issue;
}

/** Memoized hunks for a diff content item, or null when the change exceeds {@link MAX_DIFF_EDIT_DISTANCE}. */
export function getDiffHunks(diff: DiffContent): StructuredPatchHunk[] | null {
  const entry = analysisFor(diff);
  if (entry.hunks === undefined) {
    const patch = Diff.structuredPatch(
      diff.path,
      diff.path,
      diff.oldText ?? '',
      diff.newText,
      '',
      '',
      {
        context: DIFF_CONTEXT_LINES,
        maxEditLength: MAX_DIFF_EDIT_DISTANCE,
        timeout: MAX_DIFF_MILLISECONDS,
      },
    );
    entry.hunks = patch?.hunks ?? null;
    // The hunks carry every changed line, so the badge's counts come for free
    // and the counting path above never has to run for this diff. The two
    // producers of `entry.counts` must agree exactly — the tests pin that.
    if (entry.hunks) entry.counts = countHunkLines(entry.hunks);
  }
  return entry.hunks;
}

/**
 * Memoized word-diff ranges for each of the diff's hunks, aligned with
 * {@link getDiffHunks} and indexed by position within that hunk's lines.
 * Cached here rather than derived during render because the ranges depend only
 * on the hunk text — nothing about them changes when syntax tokens arrive
 * asynchronously and the view re-renders.
 */
export function getDiffWordRanges(diff: DiffContent): Array<Array<CharRange[] | undefined>> {
  const entry = analysisFor(diff);
  if (entry.wordRanges === undefined) {
    const hunks = getDiffHunks(diff);
    entry.wordRanges = hunks?.map((hunk) => computeHunkWordRanges(hunk.lines)) ?? [];
  }
  return entry.wordRanges;
}

/**
 * Memoized {@link countDiffLines} for a diff content item, reusing the hunks
 * when the diff has already been expanded.
 *
 * Null for a diff whose {@link DiffContent.originalUnavailable} is set: the new
 * text is all we have, so every count we could produce would be a count against
 * an empty file, which is a claim about the change that nothing supports. The
 * rule lives here rather than in each caller so no badge can route around it.
 */
export function getDiffLineCounts(diff: DiffContent): DiffLineCounts | null {
  if (diff.originalUnavailable) return null;
  const entry = analysisFor(diff);
  if (entry.counts === undefined) {
    entry.counts = countDiffLines(diff.oldText ?? '', diff.newText);
  }
  return entry.counts;
}

// ---------------------------------------------------------------------------
// Word-level (intra-line) diffs
// ---------------------------------------------------------------------------

/** A half-open `[start, end)` character range within a single line's text. Ranges are produced sorted and non-overlapping. */
export interface CharRange {
  start: number;
  end: number;
}

export interface WordDiffRanges {
  oldRanges: CharRange[];
  newRanges: CharRange[];
}

/** Above this fraction of either paired line's length, the lines are too dissimilar for word-level highlighting to be meaningful. */
const MAX_WORD_DIFF_CHANGED_FRACTION = 0.7;

function isSpaceCode(code: number): boolean {
  return code === 32 || code === 9 || code === 10 || code === 13;
}

/** True when `at` starts a word token in `line` — the start of the line, or preceded by whitespace, which every word tokenizer treats as a hard boundary. */
function isTokenBoundary(line: string, at: number): boolean {
  return at === 0 || isSpaceCode(line.charCodeAt(at - 1));
}

/** Extend a range list, merging into the previous entry when it's adjacent, so a line's ranges stay a handful of entries. */
function pushRange(ranges: CharRange[], start: number, end: number): void {
  const last = ranges[ranges.length - 1];
  if (last && last.end === start) last.end = end;
  else ranges.push({ start, end });
}

/**
 * Word/whitespace-level diff between one removed line and its paired added
 * line, as the changed character ranges on each side — used to render a bolder
 * highlight on just the spans that actually changed, layered over the existing
 * whole-line green/red tint.
 *
 * Returns null when the pair should render with the plain line tint instead:
 * identical text, an edit script past {@link MAX_WORD_DIFF_EDIT_DISTANCE}, or
 * lines so dissimilar that the highlight would cover most of both. That last
 * case isn't cosmetic — two lines that merely landed next to each other in a
 * replace block (an unrelated rewrite, a reordered import) produce near-
 * full-line backgrounds pocked with gaps wherever a stray character happens to
 * coincide, which reads as noise rather than as a diff.
 *
 * Only the region between the lines' common prefix and suffix is diffed. Both
 * boundaries are pulled back to whitespace, which every word tokenizer treats
 * as a hard token boundary, so the highlight still lands on whole words instead
 * of on the tail of a shared identifier.
 */
export function computeWordDiffRanges(oldLine: string, newLine: string): WordDiffRanges | null {
  if (oldLine === newLine) return null;

  // The prefix is shared character for character, so a boundary in one line is
  // the same boundary in the other; the suffix says nothing about what precedes
  // it, so it has to be a token boundary in both.
  let prefix = commonPrefixLength(oldLine, newLine);
  while (prefix > 0 && !isTokenBoundary(oldLine, prefix)) prefix--;

  const suffixLimit = Math.min(oldLine.length, newLine.length) - prefix;
  let suffix = commonSuffixLength(oldLine, newLine, suffixLimit);
  while (
    suffix > 0 &&
    !(
      isTokenBoundary(oldLine, oldLine.length - suffix) &&
      isTokenBoundary(newLine, newLine.length - suffix)
    )
  ) {
    suffix--;
  }

  const parts = Diff.diffWordsWithSpace(
    oldLine.slice(prefix, oldLine.length - suffix),
    newLine.slice(prefix, newLine.length - suffix),
    { maxEditLength: MAX_WORD_DIFF_EDIT_DISTANCE },
  );
  if (!parts) return null;

  const oldRanges: CharRange[] = [];
  const newRanges: CharRange[] = [];
  let oldChanged = 0;
  let newChanged = 0;
  let oldOffset = prefix;
  let newOffset = prefix;
  for (const part of parts) {
    const len = part.value.length;
    if (part.removed) {
      pushRange(oldRanges, oldOffset, oldOffset + len);
      oldChanged += len;
      oldOffset += len;
    } else if (part.added) {
      pushRange(newRanges, newOffset, newOffset + len);
      newChanged += len;
      newOffset += len;
    } else {
      oldOffset += len;
      newOffset += len;
    }
  }

  // Fractions are taken over the whole line, not over the diffed middle: a
  // one-word change in a long line is a small fraction of it either way.
  const oldFraction = oldLine.length > 0 ? oldChanged / oldLine.length : 0;
  const newFraction = newLine.length > 0 ? newChanged / newLine.length : 0;
  if (
    oldFraction > MAX_WORD_DIFF_CHANGED_FRACTION ||
    newFraction > MAX_WORD_DIFF_CHANGED_FRACTION
  ) {
    return null;
  }
  return { oldRanges, newRanges };
}

/** Advance past a run of `prefix`-marked lines, absorbing any `\ No newline at end of file` marker sitting inside it. */
function endOfRun(lines: readonly string[], from: number, prefix: string): number {
  let i = from;
  while (i < lines.length && (lines[i][0] === prefix || lines[i][0] === '\\')) i++;
  return i;
}

/**
 * Word-diff ranges for a hunk's lines, indexed by position within
 * `hunk.lines`. Entries are absent for every line that gets no word-level
 * highlighting: context lines, unpaired additions or removals, and pairs too
 * dissimilar to highlight.
 *
 * A run of consecutive `-` lines immediately followed by a run of `+` lines is
 * a replaced block, and pairing those runs positionally is what lets the word
 * diff compare "this line" before and after rather than comparing the whole
 * hunk. Both sides are walked in one pass with two cursors — no intermediate
 * index arrays, no pair map, and no reverse lookup.
 *
 * Two bounds keep a big hunk cheap: the pairs share a character budget, and a
 * run of pairs that all come back unhighlightable stops the walk. The second
 * one matters most in practice, because the expensive case is a wholesale block
 * rewrite, where every pair is unrelated and every word diff would be thrown
 * away — so it pays for a handful of pairs rather than for thousands.
 */
export function computeHunkWordRanges(lines: readonly string[]): Array<CharRange[] | undefined> {
  const ranges = new Array<CharRange[] | undefined>(lines.length);
  let budget = MAX_WORD_DIFF_CHARS_PER_HUNK;
  let misses = 0;
  let i = 0;
  while (i < lines.length) {
    if (lines[i][0] !== '-') {
      i++;
      continue;
    }
    const minusStart = i;
    const minusEnd = endOfRun(lines, i, '-');
    i = minusEnd;
    if (i >= lines.length || lines[i][0] !== '+') continue;
    const plusStart = i;
    const plusEnd = endOfRun(lines, i, '+');
    i = plusEnd;

    let minus = minusStart;
    let plus = plusStart;
    for (;;) {
      while (minus < minusEnd && lines[minus][0] !== '-') minus++;
      while (plus < plusEnd && lines[plus][0] !== '+') plus++;
      if (minus >= minusEnd || plus >= plusEnd) break;
      if (budget <= 0 || misses >= MAX_CONSECUTIVE_WORD_DIFF_MISSES) return ranges;
      const oldLine = lines[minus].slice(1);
      const newLine = lines[plus].slice(1);
      budget -= oldLine.length + newLine.length;
      const pair = computeWordDiffRanges(oldLine, newLine);
      if (pair) {
        ranges[minus] = pair.oldRanges;
        ranges[plus] = pair.newRanges;
        misses = 0;
      } else {
        misses++;
      }
      minus++;
      plus++;
    }
  }
  return ranges;
}

// ---------------------------------------------------------------------------
// Rendering segments
// ---------------------------------------------------------------------------

/** A run of same-styled text within a line: a syntax color and/or a word-diff highlight. */
export interface HighlightSegment {
  text: string;
  color?: string;
  highlighted: boolean;
}

/**
 * Merge a line's Shiki token boundaries with its word-diff change ranges into
 * one flat list of segments, each carrying both the token's syntax color and
 * whether it falls inside a changed range. Every segment boundary is a boundary
 * in *either* input, so no segment ever has to split its color or its highlight
 * state — that's what lets the bolder word-diff background render without
 * disturbing the syntax coloring underneath it.
 *
 * Both inputs are already sorted and non-overlapping (tokens are contiguous and
 * in order; ranges come from {@link computeWordDiffRanges}), so this is a single
 * forward walk with a cursor into each — no boundary set to build, sort, and
 * then search back into once per segment.
 */
export function splitTokensAtRanges(
  tokens: readonly ThemedToken[] | undefined,
  lineText: string,
  ranges: readonly CharRange[],
): HighlightSegment[] {
  const len = lineText.length;
  const segments: HighlightSegment[] = [];
  let tokenIdx = 0;
  let tokenEnd = 0;
  let rangeIdx = 0;
  let pos = 0;

  while (pos < len) {
    while (tokens && tokenIdx < tokens.length && tokenEnd <= pos) {
      tokenEnd += tokens[tokenIdx].content.length;
      tokenIdx++;
    }
    // Tokens can stop short of the line, or be absent entirely; whatever is
    // left over is then a single uncolored run.
    const inToken = tokens !== undefined && tokenIdx > 0 && tokenEnd > pos;
    const color = inToken ? tokens[tokenIdx - 1].color : undefined;

    while (rangeIdx < ranges.length && ranges[rangeIdx].end <= pos) rangeIdx++;
    const range = ranges[rangeIdx];
    const highlighted = range !== undefined && range.start <= pos;

    // The segment runs to the next boundary in either sequence.
    let end = inToken ? Math.min(tokenEnd, len) : len;
    if (range) end = Math.min(end, highlighted ? range.end : range.start);
    if (end <= pos) break; // unreachable for well-formed input; never spin

    segments.push({ text: lineText.slice(pos, end), color, highlighted });
    pos = end;
  }
  return segments;
}
